import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../components/default/DefaultCard';

import DefaultModal from '../../components/default/DefaultModal';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import PageHeader from '../../components/PageHeader/PageHeader';
import ButtonGeneral from '../../components/buttons/ButtonGeneral';
import '../../styles/page-components.css';
import { toast } from 'react-toastify';

import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import { AppContext } from '../../contexts/AppContext';
import useGroupService from '../../app/services/hooks/useGroupService';
import { Group, GroupFilters } from '../../app/models/Group';
import GroupDataTable from './components/tables/GroupDataTable';
import GroupCreate from './components/GroupCreate';
import GroupEdit from './components/GroupEdit';
import useNavigationPage from '../../hooks/useNavigationPage';
import GroupNotifications from './components/GroupNotifications';

const breadcrumbs = [
    { label: 'Inicio', path: '/intranet' },
    { label: 'Roles', isCurrent: true }
];

const Groups = () => {
    const history = useHistory();
    const { showLoading, hideLoading, changeAnimation } = useContext(AppContext);
    const { fetchingGetGroups, getGroups, deleteGroup, activeGroup } = useGroupService();
    const [groups, setGroups] = useState<Group[]>([]);
    const { navigationPage } = useNavigationPage();

    const [groupIdEdit, setGroupIdEdit] = useState<number>(-1);
    const [showingEdit, setShowingEdit] = useState(false);
    const [showingCreate, setShowingCreate] = useState(false);
    const [totalRows, setTotalRows] = useState<number>(0);
    const [showingNotification, setShowingNotification] = useState(false);

    const [groupFilter, setGroupFilter] = useState<GroupFilters>({
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'desc'
    });

    useEffect(() => {
        reloadTable();
    }, [groupFilter.page, groupFilter.per_page, groupFilter.sort, groupFilter.order]);

    const getAllGroups = () => {
        getGroups(groupFilter, {
            onSuccess: (response: ServiceResponse) => {
                setGroups(response.data.groups);
                setTotalRows(response.data.total_rows);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
                if (response.data.to_dashboard) {
                    navigationPage('/intranet');
                }
            }
        });
    };

    const showEdit = (groupId: number) => {
        setShowingEdit(true);
        setGroupIdEdit(groupId);
    };

    const hideEdit = () => {
        setShowingEdit(false);
        setGroupIdEdit(-1);
    };

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const reloadTable = () => {
        getAllGroups();
    };

    const destroy = (groupId: number) => {
        const _text = 'Está a punto de eliminar el rol #' + groupId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Si, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando rol...');
                        deleteGroup(groupId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });

                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'El rol no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    const active = (groupId: number, active: boolean) => {
        activeGroup(groupId, active, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().successAlert({
                    title: 'Éxito',
                    message: response.message
                });

                reloadTable();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
            }
        });
    };

    const showNotifications = (groupId: number) => {
        setShowingNotification(true);
        setGroupIdEdit(groupId);
    };

    const hideNotifications = () => {
        setShowingNotification(false);
        setGroupIdEdit(-1);
    };

    return (
        <>
            <div className="container-fluid">
                <div className="page-container">
                    {/* Header con breadcrumbs */}
                    <PageHeader
                        title=""
                        breadcrumbs={breadcrumbs}
                    />

                    {/* Header con título y botón de crear alineados */}
                    <DefaultCard>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <div>
                                <h1 className="page-title">Gestión de Roles</h1>
                                <p className="page-description">
                                    Administra los roles y permisos del sistema.{" "}
                                    <a href="#" className="description-link">
                                        Conoce más sobre la gestión de roles aquí
                                    </a>
                                </p>
                            </div>
                            <ButtonGeneral
                                onClick={showCreate}
                                title="Nuevo rol"
                                variant="primary"
                                size="small"
                                icon="fa-plus"
                                iconPosition="left"
                            />
                        </div>

                        {/* Data Table */}
                        <GroupDataTable
                            groups={groups}
                            edit={showEdit}
                            destroy={destroy}
                            notifications={showNotifications}
                            active={active}
                            loading={fetchingGetGroups}
                            filter={groupFilter}
                            setFilter={setGroupFilter}
                            totalRows={totalRows}
                            callbackSuccess={reloadTable}
                            onSearch={reloadTable}
                        />
                    </DefaultCard>
                </div>
            </div>

            {showingCreate ? (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear rol"
                    size={'lg'}
                    backdrop={true}
                    showFooter={false}
                >
                    <GroupCreate
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                        onError={hideCreate}
                    />
                </DefaultModal>
            ) : null}

            {showingEdit ? (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar rol"
                    size={'lg'}
                    backdrop={true}
                    showFooter={false}
                >
                    <GroupEdit
                        groupId={groupIdEdit}
                        onSaved={() => {
                            reloadTable();
                            hideEdit();
                        }}
                        onCancel={hideEdit}
                        onError={hideEdit}
                    />
                </DefaultModal>
            ) : null}

            {showingNotification ? (
                <DefaultModal
                    show={showingNotification}
                    handleClose={hideNotifications}
                    title="Notificaciones de Programas"
                    size={'lg'}
                    backdrop={true}
                    showFooter={false}
                >
                    <GroupNotifications
                        groupId={groupIdEdit}
                        onSaved={() => {
                            reloadTable();
                            hideNotifications();
                        }}
                        onCancel={hideNotifications}
                        onError={hideNotifications}
                    />
                </DefaultModal>
            ) : null}
        </>
    );
};

export default Groups;
